import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Collapse,
  Paper,
  Avatar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';
import { EDUGATE_API_URL } from '../../config/serviceConfig';

// 添加pulse动画样式
const pulseKeyframes = `
  @keyframes pulse {
    0%, 100% { 
      box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4);
    }
    50% { 
      box-shadow: 0 0 0 8px rgba(255, 152, 0, 0);
    }
  }
`;

// 将keyframes注入到head中
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseKeyframes;
  document.head.appendChild(style);
}

const ProjectListView = ({ 
  projects = [], 
  isLoading, 
  onRefresh, 
  onEdit, 
  onDelete, 
  onDocumentReupload,
  onViewEvaluation,
  currentUser 
}) => {
  const { t } = useTranslation();
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [error, setError] = useState('');

  const statusMap = {
    'created': { 
      label: t('yuyan_list_status_created'), 
      color: 'default', 
      icon: <RocketIcon fontSize="small" />,
      bgColor: 'rgba(255, 255, 255, 0.1)',
      textColor: '#e2e8f0',
      description: t('yuyan_list_desc_created')
    },
    'proposal_uploaded': { 
      label: t('yuyan_list_status_proposal_uploaded'), 
      color: 'info', 
      icon: <PendingIcon fontSize="small" />,
      bgColor: 'rgba(56, 189, 248, 0.1)',
      textColor: '#38bdf8',
      description: t('yuyan_list_desc_proposal_uploaded')
    },
    'proposal_evaluated': { 
      label: t('yuyan_list_status_proposal_evaluated'), 
      color: 'warning', 
      icon: <CheckCircleIcon fontSize="small" />,
      bgColor: 'rgba(251, 191, 36, 0.1)',
      textColor: '#fbbf24',
      description: t('yuyan_list_desc_proposal_evaluated')
    },
    'proposal_reviewed': { 
      label: t('yuyan_list_status_proposal_reviewed'), 
      color: 'success', 
      icon: <CheckCircleIcon fontSize="small" />,
      bgColor: 'rgba(34, 197, 94, 0.1)',
      textColor: '#4ade80',
      description: t('yuyan_list_desc_proposal_reviewed')
    },
    'conclusion_uploaded': { 
      label: t('yuyan_list_status_conclusion_uploaded'), 
      color: 'info', 
      icon: <PendingIcon fontSize="small" />,
      bgColor: 'rgba(56, 189, 248, 0.1)',
      textColor: '#38bdf8',
      description: t('yuyan_list_desc_conclusion_uploaded')
    },
    'conclusion_evaluated': { 
      label: t('yuyan_list_status_conclusion_evaluated'), 
      color: 'warning', 
      icon: <CheckCircleIcon fontSize="small" />,
      bgColor: 'rgba(251, 191, 36, 0.1)',
      textColor: '#fbbf24',
      description: t('yuyan_list_desc_conclusion_evaluated')
    },
    'conclusion_reviewed': { 
      label: t('yuyan_list_status_conclusion_reviewed'), 
      color: 'success', 
      icon: <CheckCircleIcon fontSize="small" />,
      bgColor: 'rgba(34, 197, 94, 0.1)',
      textColor: '#4ade80',
      description: t('yuyan_list_desc_conclusion_reviewed')
    }
  };

  // 项目类别颜色映射
  const categoryColors = {
    '智能硬件': '#60a5fa',
    '智慧生活': '#a78bfa',
    '数字娱乐': '#f472b6',
    '通信网络': '#34d399',
    '医疗健康': '#fbbf24',
    '人工智能': '#f87171'
  };

  // 切换项目展开状态
  const toggleProjectExpanded = (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  // 删除项目确认
  const handleDeleteConfirm = () => {
    if (selectedProject && onDelete) {
      const project = selectedProject.project || selectedProject;
      if (!project.id) {
        setError(t('yuyan_list_err_project_id'));
        setDeleteDialogOpen(false);
        setSelectedProject(null);
        return;
      }
      onDelete(selectedProject);
    }
    setDeleteDialogOpen(false);
    setSelectedProject(null);
  };

  // 检查是否可以编辑
  const canEdit = (project) => {
    return !['conclusion_reviewed'].includes(project.status);
  };

  // 检查是否可以删除
  const canDelete = (project) => {
    return true;
  };

  // 检查是否有评价数据
  const hasEvaluation = (project) => {
    return ['proposal_evaluated', 'proposal_reviewed', 'conclusion_evaluated', 'conclusion_reviewed'].includes(project.status);
  };

  // 检查是否有立项评价
  const hasProposalEvaluation = (project) => {
    return ['proposal_evaluated', 'proposal_reviewed', 'conclusion_uploaded', 'conclusion_evaluated', 'conclusion_reviewed'].includes(project.status);
  };

  // 检查是否有结题评价
  const hasConclusionEvaluation = (project) => {
    return ['conclusion_evaluated', 'conclusion_reviewed'].includes(project.status);
  };

  // 下载文档
  const handleDownloadDocument = async (doc) => {
    try {
      const documentId = doc.id || doc._id;
      if (!documentId) {
        setError(t('yuyan_list_err_doc_id'));
        return;
      }

      const response = await fetch(`${EDUGATE_API_URL}/api/yuyan/documents/${documentId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentUser.token || ''}`,
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.original_filename || '文档';
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } else {
        setError(t('yuyan_list_err_download'));
      }
    } catch (error) {
      console.error('下载文档错误:', error);
      setError(t('yuyan_list_err_network'));
    }
  };

  // 渲染项目卡片
  const renderProjectCard = (projectData) => {
    const project = projectData.project || projectData;
    const teamMembers = projectData.team_members || [];
    const documents = projectData.documents || [];
    
    const isExpanded = expandedProjects.has(project.id);
    const statusInfo = statusMap[project.status] || { 
      label: project.status, 
      color: 'default', 
      icon: <PendingIcon fontSize="small" />,
      bgColor: 'rgba(255, 255, 255, 0.1)',
      textColor: '#e2e8f0'
    };

    const categoryColor = categoryColors[project.project_category] || '#94a3b8';

    return (
      <Card 
        key={project.id} 
        sx={{ 
          mb: 3, 
          background: 'rgba(30, 41, 59, 0.6)',
          backdropFilter: 'blur(12px)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 30px -10px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(30, 41, 59, 0.8)',
          }
        }}
      >
        <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
          {/* 项目标题行 */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2} flex={1}>
              <Avatar 
                sx={{ 
                  bgcolor: `${categoryColor}20`, 
                  color: categoryColor,
                  width: 48,
                  height: 48,
                  border: `1px solid ${categoryColor}40`
                }}
              >
                <AssignmentIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600, lineHeight: 1.2, mb: 0.5 }}>
                  {project.project_name || t('yuyan_unnamed_project')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={statusInfo.label} 
                    size="small"
                    icon={statusInfo.icon}
                    sx={{ 
                      height: 24,
                      backgroundColor: statusInfo.bgColor,
                      color: statusInfo.textColor,
                      border: 'none',
                      '& .MuiChip-icon': { color: 'inherit' }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    ID: {project.id ? String(project.id).substring(0, 8) : 'Unknown'}...
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              {hasProposalEvaluation(project) && (
                <Tooltip title={t('yuyan_list_tooltip_proposal_eval')}>
                  <IconButton 
                    onClick={() => onViewEvaluation && onViewEvaluation(projectData, 'proposal')}
                    size="small"
                    sx={{ 
                      color: '#38bdf8',
                      background: 'rgba(56, 189, 248, 0.1)',
                      border: '1px solid rgba(56, 189, 248, 0.2)',
                      '&:hover': { background: 'rgba(56, 189, 248, 0.2)' }
                    }}
                  >
                    <AssessmentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {hasConclusionEvaluation(project) && (
                <Tooltip title={t('yuyan_list_tooltip_conclusion_eval')}>
                  <IconButton 
                    onClick={() => onViewEvaluation && onViewEvaluation(projectData, 'conclusion')}
                    size="small"
                    sx={{ 
                      color: '#4ade80',
                      background: 'rgba(74, 222, 128, 0.1)',
                      border: '1px solid rgba(74, 222, 128, 0.2)',
                      animation: 'pulse 2s infinite',
                      '&:hover': { background: 'rgba(74, 222, 128, 0.2)' }
                    }}
                  >
                    <AssessmentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title={isExpanded ? t('yuyan_list_tooltip_collapse') : t('yuyan_list_tooltip_expand')}>
                <IconButton 
                  onClick={() => toggleProjectExpanded(project.id)}
                  sx={{ color: '#94a3b8', transition: 'transform 0.3s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>

              {canEdit(project) && (
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onEdit) onEdit(projectData);
                  }}
                  sx={{ color: '#94a3b8', '&:hover': { color: '#38bdf8' } }}
                >
                  <EditIcon />
                </IconButton>
              )}
              
              {canDelete(project) && (
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProject(projectData);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* 基础信息网格 */}
          <Grid container spacing={2} sx={{ mb: isExpanded ? 3 : 0 }}>
            <Grid item xs={6} sm={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <SchoolIcon sx={{ fontSize: 16, color: '#64748b' }} />
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  {project.college || t('yuyan_list_unknown_college')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: categoryColor }} />
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  {project.project_category || t('yuyan_list_uncategorized')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon sx={{ fontSize: 16, color: '#64748b' }} />
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  {project.created_at ? new Date(project.created_at).toLocaleDateString() : t('yuyan_list_unknown_date')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box display="flex" alignItems="center" gap={1}>
                <GroupIcon sx={{ fontSize: 16, color: '#64748b' }} />
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  {t('yuyan_list_members_count', { count: teamMembers?.length || 0 })}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Collapse in={isExpanded}>
            <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.08)' }} />
            
            {/* 详情内容 */}
            <Grid container spacing={4}>
              {/* 左侧列：详细信息 */}
              <Grid item xs={12} md={8}>
                {/* 描述 */}
                <Box mb={3}>
                  <Typography variant="subtitle2" sx={{ color: '#e2e8f0', mb: 1, fontWeight: 600 }}>
                    {t('yuyan_list_desc')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.6 }}>
                    {project.description || t('yuyan_list_no_desc')}
                  </Typography>
                </Box>

                {/* 成员 */}
                <Box mb={3}>
                  <Typography variant="subtitle2" sx={{ color: '#e2e8f0', mb: 1.5, fontWeight: 600 }}>
                    {t('yuyan_list_team')}
                  </Typography>
                  <Grid container spacing={2}>
                    {teamMembers && teamMembers.map((member, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box sx={{ 
                          p: 1.5, 
                          borderRadius: '8px', 
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: member.role === '项目负责人' ? '#f59e0b' : '#64748b' }}>
                            {member.name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 500 }}>
                                {member.name}
                              </Typography>
                              {member.role === '项目负责人' && (
                                <Chip label={t('yuyan_list_leader_badge')} size="small" sx={{ height: 16, fontSize: 10, bgcolor: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24' }} />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              {member.major} · {member.student_id}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* 文档 */}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#e2e8f0', mb: 1.5, fontWeight: 600 }}>
                    {t('yuyan_list_docs')}
                  </Typography>
                  {documents && documents.length > 0 ? (
                    <Grid container spacing={2}>
                      {documents.map((doc, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: '8px', 
                            background: 'rgba(255, 255, 255, 0.03)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '&:hover': { background: 'rgba(255, 255, 255, 0.05)' }
                          }}>
                            <Box display="flex" alignItems="center" gap={1.5} overflow="hidden">
                              <DescriptionIcon sx={{ color: '#94a3b8' }} />
                              <Box overflow="hidden">
                                <Typography variant="body2" sx={{ color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {doc.original_filename}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  {doc.document_category === 'proposal' ? t('yuyan_list_proposal') : t('yuyan_list_conclusion')} · {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton size="small" onClick={() => handleDownloadDocument(doc)} sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0' } }}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
                      {t('yuyan_list_no_docs')}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* 右侧列：操作与评分 */}
              <Grid item xs={12} md={4}>
                {/* 评分卡片 */}
                {(project.proposal_score || project.conclusion_score) && (
                  <Box sx={{ mb: 3, p: 2, borderRadius: '12px', background: 'rgba(15, 23, 42, 0.4)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 2 }}>
                      {t('yuyan_list_ai_result')}
                    </Typography>
                    {project.proposal_score && (
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={project.conclusion_score ? 1.5 : 0}>
                        <Typography variant="body2" sx={{ color: '#e2e8f0' }}>{t('yuyan_list_proposal_review')}</Typography>
                        <Box textAlign="right">
                          <Typography variant="h6" sx={{ color: '#38bdf8', fontWeight: 600, lineHeight: 1 }}>
                            {project.proposal_score}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {project.proposal_level}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {project.conclusion_score && (
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ color: '#e2e8f0' }}>{t('yuyan_list_conclusion_review')}</Typography>
                        <Box textAlign="right">
                          <Typography variant="h6" sx={{ color: '#4ade80', fontWeight: 600, lineHeight: 1 }}>
                            {project.conclusion_score}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {project.conclusion_level}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* 操作按钮组 */}
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {!documents.some(doc => doc.document_category === 'proposal') && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<UploadIcon />}
                      onClick={() => onDocumentReupload && onDocumentReupload(projectData, 'proposal')}
                      sx={{ 
                        borderColor: '#38bdf8', 
                        color: '#38bdf8',
                        '&:hover': { borderColor: '#0ea5e9', background: 'rgba(56, 189, 248, 0.1)' }
                      }}
                    >
                      {t('yuyan_list_upload_proposal')}
                    </Button>
                  )}
                  
                  {documents.some(doc => doc.document_category === 'proposal') && (
                    <Button
                      variant="text"
                      fullWidth
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={() => onDocumentReupload && onDocumentReupload(projectData, 'proposal')}
                      sx={{ color: '#64748b', '&:hover': { color: '#94a3b8', background: 'rgba(255, 255, 255, 0.05)' } }}
                    >
                      {t('yuyan_list_update_proposal')}
                    </Button>
                  )}
                  
                  {documents.some(doc => doc.document_category === 'proposal') && 
                   !documents.some(doc => doc.document_category === 'conclusion') && (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<UploadIcon />}
                      onClick={() => onDocumentReupload && onDocumentReupload(projectData, 'conclusion')}
                      sx={{ 
                        borderColor: '#4ade80', 
                        color: '#4ade80',
                        '&:hover': { borderColor: '#22c55e', background: 'rgba(74, 222, 128, 0.1)' }
                      }}
                    >
                      {t('yuyan_list_upload_conclusion')}
                    </Button>
                  )}

                   {documents.some(doc => doc.document_category === 'conclusion') && (
                    <Button
                      variant="text"
                      fullWidth
                      size="small"
                      startIcon={<UploadIcon />}
                      onClick={() => onDocumentReupload && onDocumentReupload(projectData, 'conclusion')}
                      sx={{ color: '#64748b', '&:hover': { color: '#94a3b8', background: 'rgba(255, 255, 255, 0.05)' } }}
                    >
                      {t('yuyan_list_update_conclusion')}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={40} sx={{ color: '#38bdf8', mb: 2 }} />
        <Typography variant="body2" color="#94a3b8">
          {t('yuyan_list_loading')}
        </Typography>
      </Box>
    );
  }

  if (projects.length === 0) {
    return (
      <Box 
        textAlign="center" 
        py={8} 
        sx={{ 
          background: 'rgba(255, 255, 255, 0.02)', 
          borderRadius: '16px', 
          border: '1px dashed rgba(255, 255, 255, 0.1)' 
        }}
      >
        <RocketIcon sx={{ fontSize: 64, color: '#334155', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#94a3b8', mb: 1 }}>
          {t('yuyan_list_empty')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {t('yuyan_list_empty_hint')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, background: 'rgba(239, 68, 68, 0.1)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
          {error}
        </Alert>
      )}

      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" sx={{ color: '#e2e8f0', fontWeight: 600 }}>
          {t('yuyan_list_my_projects')}
          <Chip 
            label={projects.length} 
            size="small" 
            sx={{ ml: 1.5, bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', height: 20, fontSize: 11 }} 
          />
        </Typography>
      </Box>

      <Box>
        {projects.map(renderProjectCard)}
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: '#1e293b',
            color: '#f8fafc',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{t('yuyan_list_confirm_delete_title')}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            {t('yuyan_list_confirm_delete_msg', { name: (selectedProject?.project || selectedProject)?.project_name || '' })}
          </Typography>
          <Typography variant="body2" sx={{ color: '#ef4444', mt: 1 }}>
            {t('yuyan_list_confirm_delete_warn')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#94a3b8' }}>
            {t('yuyan_list_btn_cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('yuyan_list_btn_confirm_delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectListView;